import React, { useEffect, useState, useCallback } from 'react';
import { Flex, Spin, DatePicker, Typography, Select } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import dayjs from 'dayjs';
import { Fade } from 'react-awesome-reveal';
import { useTranslation } from 'react-i18next';
import EmissionService from '../../services/EmissionService';
import Header from '../components/DataPage/Header';
import back from '../../images/back@3x.png';
import { clrs } from '../../constants/colors';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const REFRESH_INTERVAL = 60000;

const formatTime = (iso) => iso ? dayjs(iso).format('DD.MM.YYYY HH:mm') : '—';
const fmt = (v) => (v != null && v !== '—') ? (+v).toFixed(2) : '—';

/* emission name → i18n key */
const EMISSION_I18N = {
    CO_gs:     'emission.CO_gs',
    CO_mgm3:   'emission.CO_mgm3',
    DUST:      'emission.DUST',
    DUST_gs:   'emission.DUST',
    FLOW:      'emission.FLOW',
    HUMIDITY:  'emission.HUMIDITY',
    NO_gs:     'emission.NO_gs',
    NO_mgm3:   'emission.NO_mgm3',
    NO2_gs:    'emission.NO2_gs',
    NO2_mgm3:  'emission.NO2_mgm3',
    NOx_gs:    'emission.NOx_gs',
    NOx_mgm3:  'emission.NOx_mgm3',
    O2:        'emission.O2',
    PT:        'emission.PT',
    SO2_gs:    'emission.SO2_gs',
    SO2_mgm3:  'emission.SO2_mgm3',
    TEMP:      'emission.TEMP',
    TEMP_LINE: 'emission.TEMP',
};

const AVG_TYPES = [
    { value: 'MINUTE', label: 'Минута' },
    { value: 'HOUR',   label: 'Час' },
    { value: 'DAY',    label: 'День' },
];

/* actual мг/м³ keys + labels */
const MG_KEYS   = ['no', 'no2', 'co', 'so2', 'dust'];
const MG_LABELS = { no: 'NO', no2: 'NO₂', co: 'CO', so2: 'SO₂', dust: 'Пыль' };

/* actual г/с keys + labels */
const ENV_KEYS   = ['temp', 'hum', 'o2', 'pt'];
const ENV_LABELS = { temp: 'Температура °C', hum: 'Влажность %', o2: 'O₂ %', pt: 'Давление кПа' };

/* build Highcharts series from actual chart data */
const buildActualSeries = (data, keys, labels) =>
    keys.map((key) => ({
        name: labels[key],
        data: data
            .filter((d) => d[key] != null)
            .map((d) => [Date.parse(d.time + 'Z'), +Number(d[key]).toFixed(2)]),
    }));

/* build Highcharts series from trend data (archive) */
const buildTrendSeries = (data, seriesName) => [{
    name: seriesName,
    data: data
        .filter((d) => d.value != null)
        .map((d) => [Date.parse(d.timestamp + 'Z'), +Number(d.value).toFixed(2)]),
}];

/* shared Highcharts lang setup */
const setHighchartsLang = (t) => {
    Highcharts.setOptions({
        lang: {
            months: [1,2,3,4,5,6,7,8,9,10,11,12].map((m) => t(`dataPage.analytics.months.${m}`)),
            shortMonths: [1,2,3,4,5,6,7,8,9,10,11,12].map((m) => t(`dataPage.analytics.months.${m}.short`)),
        },
    });
};

/* ─── Highcharts areaspline chart ─── */
const DashboardChart = ({ series, title, yAxisTitle }) => {
    const options = {
        chart: { type: 'areaspline', height: 400, backgroundColor: 'transparent' },
        title: { text: title, style: { fontSize: '14px', fontWeight: '700', color: clrs.blacker } },
        xAxis: { type: 'datetime', title: { text: '' } },
        yAxis: { title: { text: yAxisTitle } },
        tooltip: {
            shared: true,
            formatter: function () {
                const header = '<b>' + Highcharts.dateFormat('%d %b %Y %H:%M', this.x) + '</b><br/>';
                const pts = this.points.map((p) =>
                    `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${p.y}</b>`
                ).join('<br/>');
                return header + pts;
            },
        },
        plotOptions: {
            areaspline: { fillOpacity: 0.25 },
            series: { marker: { enabled: true, radius: 1, symbol: 'circle', lineWidth: 1, lineColor: null, fillColor: '#fff' } },
        },
        series,
        credits: { enabled: false },
    };

    return (
        <Flex style={{ width: '100%', position: 'relative' }}>
            <HighchartsReact
                highcharts={Highcharts}
                options={options}
                containerProps={{ style: { width: '100%', borderRadius: 10, borderBottom: `3px solid ${clrs.whiterBlacker}` } }}
            />
            <div style={{ backgroundColor: 'white', width: 150, height: '1rem', position: 'absolute', bottom: 3, right: 3, borderRadius: 10 }} />
        </Flex>
    );
};

/* ─── small reusable UI ─── */
const StatCard = ({ label, main, sub, grid }) => (
    <div style={{
        backgroundColor: 'rgba(255,255,255,0.92)',
        borderRadius: 16, padding: '24px 28px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
        borderBottom: `4px solid ${clrs.orange}`,
        flex: 1, minWidth: 240,
    }}>
        <Flex vertical gap={6}>
            <div style={{ fontSize: 11, fontWeight: 600, color: clrs.grey, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: clrs.blacker, lineHeight: 1.2 }}>{main}</div>
            {sub && <div style={{ fontSize: 11, color: clrs.grey }}>{sub}</div>}
            {grid && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px', marginTop: 8 }}>
                    {grid.map(({ label: l, value: v }) => (
                        <Flex key={l} vertical gap={0}>
                            <span style={{ fontSize: 11, color: clrs.grey }}>{l}</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: clrs.blacker }}>{v}</span>
                        </Flex>
                    ))}
                </div>
            )}
        </Flex>
    </div>
);

const SectionCard = ({ title, accentColor, children }) => (
    <div style={{
        backgroundColor: 'rgba(255,255,255,0.92)',
        borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
        borderBottom: `4px solid ${accentColor}`, overflow: 'hidden',
    }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0', fontWeight: 800, fontSize: 15, color: clrs.blacker }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: accentColor, marginRight: 8 }} />
            {title}
        </div>
        <div style={{ padding: '20px 24px' }}>{children}</div>
    </div>
);

const TabBtn = ({ active, onClick, children }) => (
    <div onClick={onClick} style={{
        padding: '10px 28px', borderRadius: 10, fontWeight: 700, fontSize: 14,
        cursor: 'pointer', userSelect: 'none', transition: 'all 0.2s',
        backgroundColor: active ? clrs.orange : 'rgba(255,255,255,0.85)',
        color: active ? 'white' : clrs.blacker,
        boxShadow: active ? '0 4px 14px rgba(255,76,21,0.35)' : 'none',
    }}>
        {children}
    </div>
);

const PeriodBar = ({ dateRange, onChange, accentColor }) => (
    <Flex align="center" gap={12} wrap="wrap">
        <div style={{ backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: 10, padding: '10px 18px', fontWeight: 700, fontSize: 13, color: clrs.blacker }}>
            Период:
        </div>
        <RangePicker
            format="DD.MM.YYYY"
            value={dateRange}
            onChange={onChange}
            allowClear
            style={{ borderRadius: 10 }}
            placeholder={['Начало', 'Конец']}
        />
        {dateRange && (
            <div onClick={() => onChange(null)} style={{
                backgroundColor: accentColor ?? clrs.orange, color: 'white',
                borderRadius: 10, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}>
                Сбросить
            </div>
        )}
    </Flex>
);

/* ─── Actual tab ─── */
const ActualTab = () => {
    const { t } = useTranslation();
    setHighchartsLang(t);

    const DEFAULT_RANGE = [dayjs('2025-03-01'), dayjs('2025-03-31')];
    const [summary,      setSummary]      = useState(null);
    const [chartData,    setChartData]    = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [chartLoading, setChartLoading] = useState(false);
    const [lastRefresh,  setLastRefresh]  = useState(null);
    const [dateRange,    setDateRange]    = useState(DEFAULT_RANGE);

    const fetchChart = useCallback(async (range) => {
        setChartLoading(true);
        try {
            const res = range
                ? await EmissionService.getDashboardChartRange(
                    range[0].format('YYYY-MM-DDT00:00:00'),
                    range[1].format('YYYY-MM-DDT23:59:59'),
                  )
                : await EmissionService.getDashboardChart(200);
            setChartData(res.data);
        } catch (_) {
        } finally {
            setChartLoading(false);
        }
    }, []);

    const fetchAll = useCallback(async (range) => {
        try {
            const [sRes, cRes] = await Promise.all([
                EmissionService.getDashboardSummary(),
                range
                    ? EmissionService.getDashboardChartRange(range[0].format('YYYY-MM-DDT00:00:00'), range[1].format('YYYY-MM-DDT23:59:59'))
                    : EmissionService.getDashboardChart(200),
            ]);
            setSummary(sRes.data);
            setChartData(cRes.data);
            setLastRefresh(new Date());
        } catch (_) {
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll(DEFAULT_RANGE);
        const timer = setInterval(() => fetchAll(DEFAULT_RANGE), REFRESH_INTERVAL);
        return () => clearInterval(timer);
    }, [fetchAll]);

    const handleRange = (range) => { setDateRange(range); fetchChart(range ?? DEFAULT_RANGE); };

    if (loading) return <Flex justify="center" align="center" style={{ height: 400 }}><Spin size="large" /></Flex>;

    const uptime = summary?.uptime;
    const last   = summary?.lastMeasurement;
    const maxVal = summary?.maxValues;

    const mgSeries = buildActualSeries(chartData, MG_KEYS, MG_LABELS);
    const envSeries = buildActualSeries(chartData, ENV_KEYS, ENV_LABELS);

    return (
        <Flex vertical gap={24}>
            <Flex justify="flex-end">
                <div style={{ backgroundColor: 'rgba(255,255,255,0.85)', borderRadius: 10, padding: '7px 14px', fontSize: 12, color: clrs.grey }}>
                    <ReloadOutlined style={{ color: clrs.green, marginRight: 6 }} />
                    Авто-обновление каждые 60 сек.
                    {lastRefresh && ` · ${dayjs(lastRefresh).format('HH:mm:ss')}`}
                </div>
            </Flex>

            <Flex gap={20} wrap="wrap">
                <StatCard
                    label="Время работы системы"
                    main={uptime?.formatted ?? '—'}
                    grid={[
                        { label: 'Первый замер',    value: formatTime(uptime?.firstRecordTime) },
                        { label: 'Последний замер', value: formatTime(uptime?.lastRecordTime) },
                        { label: 'Всего дней',      value: uptime?.totalDays ?? '—' },
                        { label: 'Всего замеров',   value: summary?.totalRecords ?? '—' },
                    ]}
                />
                <StatCard
                    label="Последний замер"
                    main={formatTime(last?.time)}
                    grid={last ? [
                        { label: 'NO мг/м³',   value: fmt(last.no)    },
                        { label: 'NO г/с',     value: fmt(last.noGs)  },
                        { label: 'NO₂ мг/м³',  value: fmt(last.no2)   },
                        { label: 'NO₂ г/с',    value: fmt(last.no2Gs) },
                        { label: 'CO мг/м³',   value: fmt(last.co)    },
                        { label: 'CO г/с',     value: fmt(last.coGs)  },
                        { label: 'SO₂ мг/м³',  value: fmt(last.so2)   },
                        { label: 'Пыль мг/м³', value: fmt(last.dust)  },
                        { label: 'O₂ %',       value: fmt(last.o2)    },
                        { label: 'Темп. °C',   value: fmt(last.temp)  },
                    ] : null}
                />
                <StatCard
                    label="Максимальные значения"
                    main={maxVal?.maxCo != null ? `CO: ${fmt(maxVal.maxCo)} мг/м³` : '—'}
                    sub="Максимум CO за весь период"
                    grid={maxVal ? [
                        { label: 'NO макс.',   value: `${fmt(maxVal.maxNo)}   мг/м³` },
                        { label: 'NO₂ макс.',  value: `${fmt(maxVal.maxNo2)}  мг/м³` },
                        { label: 'SO₂ макс.',  value: `${fmt(maxVal.maxSo2)}  мг/м³` },
                        { label: 'Пыль макс.', value: `${fmt(maxVal.maxDust)} мг/м³` },
                    ] : null}
                />
            </Flex>

            <PeriodBar dateRange={dateRange} onChange={handleRange} />

            {chartLoading ? (
                <Flex justify="center" align="center" style={{ height: 400 }}><Spin size="large" /></Flex>
            ) : chartData.length > 0 ? (
                <>
                    <SectionCard title="Концентрация загрязнителей (мг/м³)" accentColor={clrs.orange}>
                        <DashboardChart series={mgSeries} title="NO, NO₂, CO, SO₂, Пыль" yAxisTitle="мг/м³" />
                    </SectionCard>
                    <SectionCard title="Физические параметры (температура, влажность, O₂, давление)" accentColor={clrs.blue}>
                        <DashboardChart series={envSeries} title="Температура, Влажность, O₂, Давление" yAxisTitle="Значение" />
                    </SectionCard>
                </>
            ) : (
                <Flex justify="center" align="center" style={{ height: 200 }}>
                    <Text type="secondary">Нет данных для графика</Text>
                </Flex>
            )}
        </Flex>
    );
};

/* ─── Archive tab ─── */
const ArchiveTab = () => {
    const { t } = useTranslation();
    setHighchartsLang(t);

    const ARCHIVE_DEFAULT_RANGE = [dayjs('2023-12-16'), dayjs('2023-12-31')];
    const [summary,      setSummary]      = useState(null);
    const [loading,      setLoading]      = useState(true);
    const [selectedName, setSelectedName] = useState('CO_mgm3');
    const [avgType,      setAvgType]      = useState('DAY');
    const [dateRange,    setDateRange]    = useState(ARCHIVE_DEFAULT_RANGE);
    const [trendData,    setTrendData]    = useState([]);
    const [trendLoading, setTrendLoading] = useState(false);

    const fetchTrend = useCallback(async (name, type, range) => {
        if (!name) return;
        setTrendLoading(true);
        try {
            const res = await EmissionService.getTrends(
                name,
                range ? range[0].format('YYYY-MM-DDT00:00:00') : undefined,
                range ? range[1].format('YYYY-MM-DDT23:59:59') : undefined,
                type,
            );
            setTrendData(res.data?.list ?? res.data ?? []);
        } catch (_) {
        } finally {
            setTrendLoading(false);
        }
    }, []);

    useEffect(() => {
        EmissionService.getArchiveDashboardSummary()
            .then((res) => setSummary(res.data))
            .catch(() => {})
            .finally(() => setLoading(false));
        fetchTrend('CO_mgm3', 'DAY', ARCHIVE_DEFAULT_RANGE);
    }, [fetchTrend]);

    const emissionName = (name) => EMISSION_I18N[name] ? t(EMISSION_I18N[name]) : name;

    const handleSelect = (name) => { setSelectedName(name); fetchTrend(name, avgType, dateRange); };
    const handleAvg    = (type) => { setAvgType(type);      fetchTrend(selectedName, type, dateRange); };
    const handleRange  = (range) => {
        const r = range ?? ARCHIVE_DEFAULT_RANGE;
        setDateRange(r);
        fetchTrend(selectedName, avgType, r);
    };

    if (loading) return <Flex justify="center" align="center" style={{ height: 400 }}><Spin size="large" /></Flex>;

    const uptime = summary?.uptime;
    const stats  = summary?.emissionStats ?? [];
    const trendSeries = buildTrendSeries(trendData, emissionName(selectedName));

    return (
        <Flex vertical gap={24}>
            <Flex gap={20} wrap="wrap">
                <StatCard
                    label="Архивные данные (блокчейн)"
                    main={uptime?.formatted ?? '—'}
                    grid={[
                        { label: 'Первый замер',    value: formatTime(uptime?.firstRecordTime) },
                        { label: 'Последний замер', value: formatTime(uptime?.lastRecordTime) },
                        { label: 'Всего дней',      value: uptime?.totalDays ?? '—' },
                        { label: 'Всего записей',   value: summary?.totalRecords?.toLocaleString() ?? '—' },
                    ]}
                />
            </Flex>

            {/* emission stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
                {stats.map((s) => {
                    const active = selectedName === s.name;
                    return (
                        <div key={s.name} onClick={() => handleSelect(s.name)} style={{
                            backgroundColor: active ? 'rgba(255,76,21,0.08)' : 'rgba(255,255,255,0.92)',
                            borderRadius: 14, padding: '18px 20px',
                            boxShadow: active ? '0 4px 20px rgba(255,76,21,0.25)' : '0 2px 12px rgba(0,0,0,0.07)',
                            borderBottom: `4px solid ${active ? clrs.orange : '#e0e0e0'}`,
                            cursor: 'pointer', transition: 'all 0.2s',
                        }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: active ? clrs.orange : clrs.grey, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                                {emissionName(s.name)}
                            </div>
                            <div style={{ fontSize: 11, color: clrs.grey, marginBottom: 10 }}>{s.name}</div>
                            <Flex vertical gap={4}>
                                <Flex justify="space-between">
                                    <span style={{ fontSize: 11, color: clrs.grey }}>Последнее</span>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: clrs.blacker }}>{fmt(s.lastValue)}</span>
                                </Flex>
                                <Flex justify="space-between">
                                    <span style={{ fontSize: 11, color: clrs.grey }}>Максимум</span>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: clrs.orange }}>{fmt(s.maxValue)}</span>
                                </Flex>
                                <Flex justify="space-between">
                                    <span style={{ fontSize: 11, color: clrs.grey }}>Записей</span>
                                    <span style={{ fontSize: 12, color: clrs.grey }}>{s.totalRecords?.toLocaleString() ?? '—'}</span>
                                </Flex>
                                <Flex justify="space-between">
                                    <span style={{ fontSize: 11, color: clrs.grey }}>Замер</span>
                                    <span style={{ fontSize: 11, color: clrs.grey }}>{formatTime(s.lastTimestamp)}</span>
                                </Flex>
                            </Flex>
                        </div>
                    );
                })}
            </div>

            {/* chart controls */}
            <Flex align="center" gap={12} wrap="wrap">
                <div style={{ backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: 10, padding: '10px 18px', fontWeight: 700, fontSize: 13, color: clrs.blacker }}>
                    {selectedName ? `График: ${emissionName(selectedName)}` : 'Выберите эмиссию'}
                </div>
                <Select value={avgType} onChange={handleAvg} options={AVG_TYPES} style={{ width: 110 }} />
                <PeriodBar dateRange={dateRange} onChange={handleRange} accentColor={clrs.blue} />
            </Flex>

            {/* trend chart */}
            <SectionCard title={`Тренд: ${emissionName(selectedName)}`} accentColor={clrs.blue}>
                {trendLoading ? (
                    <Flex justify="center" align="center" style={{ height: 400 }}><Spin size="large" /></Flex>
                ) : trendData.length > 0 ? (
                    <DashboardChart series={trendSeries} title={emissionName(selectedName)} yAxisTitle="Значение" />
                ) : (
                    <Flex justify="center" align="center" style={{ height: 200 }}>
                        <Text type="secondary">Нет данных для графика</Text>
                    </Flex>
                )}
            </SectionCard>
        </Flex>
    );
};

/* ─── Main page ─── */
const DashboardPage = () => {
    const [tab, setTab] = useState('actual');

    return (
        <div style={{ width: '100%', minHeight: '100vh', backgroundImage: `url(${back})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <Header />
            <div style={{ paddingTop: 140, paddingBottom: 60, paddingLeft: 25, paddingRight: 25 }}>
                <div style={{ maxWidth: 1440, margin: '0 auto' }}>
                    <Flex vertical gap={28}>

                        <Fade triggerOnce>
                            <div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: clrs.blacker, lineHeight: 1 }}>Дашборд</div>
                                <div style={{ fontSize: '2.8rem', fontWeight: 900, color: clrs.orange, lineHeight: 1.1 }}>МОНИТОРИНГ ВЫБРОСОВ</div>
                            </div>
                        </Fade>

                        <Fade triggerOnce>
                            <Flex gap={12}>
                                <TabBtn active={tab === 'actual'}  onClick={() => setTab('actual')}>Актуальные данные</TabBtn>
                                <TabBtn active={tab === 'archive'} onClick={() => setTab('archive')}>Архивные данные</TabBtn>
                            </Flex>
                        </Fade>

                        {tab === 'actual'  && <ActualTab />}
                        {tab === 'archive' && <ArchiveTab />}

                    </Flex>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
